import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { formatDate } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { CONTRACT_STATUS } from '@/modules/Contract/constants'
import { Subcontract } from '../../types'
import {
    Building2,
    UserCheck,
    CalendarRange,
    FileText,
    Server,
} from 'lucide-react'

interface SubcontractInfoCardProps {
    subcontract: Subcontract
    contractID: string | undefined
}

const SubcontractInfoCard = ({ subcontract }: SubcontractInfoCardProps) => {
    const { t } = useTranslation()

    const status = CONTRACT_STATUS(t).find(
        (s) => s.value === subcontract.subcontract_status
    )
    const services = subcontract.services || []
    const primaryService = services[0]

    return (
        <div className="space-y-5">
            {/* Header row */}
            <div className="flex items-center gap-2.5">
                <Typogaphy className="font-medium text-primary">
                    {subcontract.subcontract_code}
                </Typogaphy>
                {status && (
                    <Chip
                        label={status.label}
                        size="small"
                        variant="contained"
                        color={status.color as any}
                    />
                )}
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCell
                    icon={<Building2 className="w-4 h-4" />}
                    label={t('contract_owner')}
                    value={
                        subcontract.customer_id ? (
                            <Link
                                to={generatePathDatacentral(
                                    paths.customerDetail,
                                    { id: subcontract.customer_id }
                                )}
                                className="text-primary hover:underline text-sm"
                            >
                                {subcontract.company_name ||
                                    subcontract.customer_name}
                            </Link>
                        ) : (
                            subcontract.company_name ||
                            subcontract.customer_name
                        )
                    }
                />

                <InfoCell
                    icon={<UserCheck className="w-4 h-4" />}
                    label={t('contract_employee')}
                    value={subcontract.employee_name}
                />

                <InfoCell
                    icon={<FileText className="w-4 h-4" />}
                    label={t('subcontract_name')}
                    value={subcontract.subcontract_name}
                />

                <InfoCell
                    icon={<CalendarRange className="w-4 h-4" />}
                    label={t('subcontract_effective_date')}
                    value={
                        subcontract.subcontract_effective_date
                            ? formatDate(subcontract.subcontract_effective_date)
                            : undefined
                    }
                    suffix={
                        subcontract.subcontract_expiry_date ? (
                            <span className="text-xs text-muted-foreground ml-1">
                                {' - '}
                                {formatDate(
                                    subcontract.subcontract_expiry_date
                                )}
                            </span>
                        ) : undefined
                    }
                />

                {services.length === 1 && primaryService && (
                    <InfoCell
                        icon={<Server className="w-4 h-4" />}
                        label={t('contract_service')}
                        value={
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    { id: primaryService.service_id }
                                )}
                                className="text-primary hover:underline text-sm"
                            >
                                {primaryService.service_name}
                            </Link>
                        }
                    />
                )}

                {services.length > 1 && (
                    <InfoCell
                        icon={<Server className="w-4 h-4" />}
                        label={t('contract_services')}
                        value={`${services.length} ${t('services_count')}`}
                    />
                )}
            </div>

            {/* Note section */}
            {subcontract.subcontract_note && (
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                    <Typogaphy className="text-xs text-muted-foreground mb-1">
                        {t('subcontract_note')}
                    </Typogaphy>
                    <Typogaphy className="text-sm text-foreground/80 leading-relaxed">
                        {subcontract.subcontract_note}
                    </Typogaphy>
                </div>
            )}
        </div>
    )
}

/** Single info cell with icon, label on top, value below */
const InfoCell = ({
    icon,
    label,
    value,
    suffix,
}: {
    icon: React.ReactNode
    label: string
    value?: React.ReactNode
    suffix?: React.ReactNode
}) => (
    <div className="flex items-start gap-3 rounded-md bg-gray-50/10 px-3.5 py-2.5">
        <div className="mt-0.5 text-muted-foreground/60">{icon}</div>
        <div className="min-w-0 flex-1">
            <Typogaphy className="text-xs text-muted-foreground uppercase mb-0.5">
                {label}
            </Typogaphy>
            <div className="text-sm text-foreground truncate">
                {value || <span className="text-muted-foreground/40">---</span>}
                {suffix}
            </div>
        </div>
    </div>
)

export default SubcontractInfoCard
